import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dialog",
    "scheduleDialog",
    "eventTitle",
    "eventTime",
    "eventLocation",
    "eventDescription",
    "eventActions",
    "nowLine"
  ]
  static values = {
    startHour: Number,
    endHour: Number,
    timeZone: String,
    openInquiryLabel: String,
    rescheduleViewingLabel: String,
    cancelViewingLabel: String,
    cancelConfirm: String,
    openScheduleOnConnect: Boolean,
    prefillDate: String,
    prefillTime: String
  }

  connect() {
    this.updateNowLine()
    this.nowTimer = window.setInterval(() => this.updateNowLine(), 60_000)

    if (this.openScheduleOnConnectValue) {
      this.openScheduleDialog({
        date: this.prefillDateValue,
        time: this.prefillTimeValue
      })
    }
  }

  disconnect() {
    window.clearInterval(this.nowTimer)
  }

  routeClick(event) {
    const viewingButton = event.target.closest("[data-calendar-kind='viewing']")
    if (viewingButton) {
      event.preventDefault()
      event.stopPropagation()
      this.openViewingFromElement(viewingButton)
      return
    }

    const externalButton = event.target.closest("[data-calendar-kind='external']")
    if (externalButton) {
      event.preventDefault()
      event.stopPropagation()
      this.openEventFromElement(externalButton)
    }
  }

  openEvent(event) {
    event.preventDefault()
    this.openEventFromElement(event.currentTarget)
  }

  openViewing(event) {
    event.preventDefault()
    this.openViewingFromElement(event.currentTarget)
  }

  openSchedule(event) {
    event?.preventDefault()
    this.openScheduleDialog()
  }

  openScheduleSlot(event) {
    event.preventDefault()
    const button = event.currentTarget
    this.openScheduleDialog({
      date: button.dataset.scheduleDate,
      time: button.dataset.scheduleTime
    })
  }

  openScheduleDialog({ date, time } = {}) {
    if (!this.hasScheduleDialogTarget) return

    this.prefillScheduleForm(date, time)
    this.scheduleDialogTarget.showModal()
    this.cleanScheduleQueryParams()
  }

  cleanScheduleQueryParams() {
    const url = new URL(window.location.href)
    if (!url.searchParams.has("schedule") && !url.searchParams.has("date")) return

    url.searchParams.delete("schedule")
    url.searchParams.delete("date")
    url.searchParams.delete("time")
    url.searchParams.delete("inquiry_id")
    history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`)
  }

  prefillScheduleForm(date, time) {
    const dateField = this.scheduleDialogTarget.querySelector("#viewing-date")
    const timeField = this.scheduleDialogTarget.querySelector("#viewing-time")

    if (dateField && date) dateField.value = date
    if (timeField && time) timeField.value = time

    dateField?.dispatchEvent(new Event("change", { bubbles: true }))
  }

  openEventFromElement(button) {
    this.populateDialog({
      title: button.dataset.eventSummary || "",
      time: button.dataset.eventTime || "",
      location: button.dataset.eventLocation,
      description: button.dataset.eventDescription
    })
    this.clearActions()
    this.dialogTarget.showModal()
  }

  openViewingFromElement(button) {
    this.populateDialog({
      title: button.dataset.viewingTitle || "",
      time: button.dataset.viewingTime || "",
      location: button.dataset.viewingLocation,
      description: button.dataset.viewingDescription
    })
    this.renderViewingActions(button)
    this.dialogTarget.showModal()
  }

  populateDialog({ title, time, location, description }) {
    this.eventTitleTarget.textContent = title
    this.eventTimeTarget.textContent = time
    this.setOptionalField(this.eventLocationTarget, location)
    this.setOptionalField(this.eventDescriptionTarget, description)
  }

  clearActions() {
    if (!this.hasEventActionsTarget) return

    this.eventActionsTarget.innerHTML = ""
    this.eventActionsTarget.hidden = true
  }

  renderViewingActions(button) {
    if (!this.hasEventActionsTarget) return

    this.eventActionsTarget.innerHTML = ""
    const inquiryUrl = button.dataset.viewingInquiryUrl
    const rescheduleUrl = button.dataset.viewingRescheduleUrl
    const cancelUrl = button.dataset.viewingCancelUrl
    if (!inquiryUrl && !rescheduleUrl && !cancelUrl) {
      this.eventActionsTarget.hidden = true
      return
    }

    if (rescheduleUrl) {
      const link = document.createElement("a")
      link.href = rescheduleUrl
      link.className = "pro-btn pro-btn--primary pro-btn--small"
      link.textContent = this.rescheduleViewingLabelValue
      this.eventActionsTarget.appendChild(link)
    }

    if (inquiryUrl) {
      const link = document.createElement("a")
      link.href = inquiryUrl
      link.className = rescheduleUrl ? "pro-btn pro-btn--secondary pro-btn--small" : "pro-btn pro-btn--primary pro-btn--small"
      link.textContent = this.openInquiryLabelValue
      this.eventActionsTarget.appendChild(link)
    }

    if (cancelUrl) {
      const form = document.createElement("form")
      form.method = "post"
      form.action = cancelUrl
      form.dataset.turbo = "false"
      form.className = "inline"

      const method = document.createElement("input")
      method.type = "hidden"
      method.name = "_method"
      method.value = "patch"
      form.appendChild(method)

      const token = document.querySelector('meta[name="csrf-token"]')?.content
      if (token) {
        const csrf = document.createElement("input")
        csrf.type = "hidden"
        csrf.name = "authenticity_token"
        csrf.value = token
        form.appendChild(csrf)
      }

      const submit = document.createElement("button")
      submit.type = "submit"
      submit.className = "pro-btn pro-btn--secondary pro-btn--small"
      submit.textContent = this.cancelViewingLabelValue
      if (this.cancelConfirmValue) {
        submit.addEventListener("click", (clickEvent) => {
          if (!window.confirm(this.cancelConfirmValue)) {
            clickEvent.preventDefault()
          }
        })
      }
      form.appendChild(submit)
      this.eventActionsTarget.appendChild(form)
    }

    this.eventActionsTarget.hidden = false
  }

  setOptionalField(target, value) {
    const text = (value || "").trim()
    if (text) {
      target.textContent = text
      target.hidden = false
    } else {
      target.textContent = ""
      target.hidden = true
    }
  }

  updateNowLine() {
    if (!this.hasNowLineTarget) return

    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: this.timeZoneValue,
      hour: "numeric",
      minute: "numeric",
      hour12: false
    })
    const parts = formatter.formatToParts(new Date())
    const hour = Number(parts.find((part) => part.type === "hour")?.value || 0)
    const minute = Number(parts.find((part) => part.type === "minute")?.value || 0)
    const totalMinutes = (this.endHourValue - this.startHourValue) * 60
    const offsetMinutes = (hour - this.startHourValue) * 60 + minute
    const percent = (offsetMinutes / totalMinutes) * 100
    const visible = offsetMinutes >= 0 && offsetMinutes <= totalMinutes

    this.nowLineTarget.style.top = `${percent}%`
    this.nowLineTarget.hidden = !visible
  }
}
